import {
    BookOpenIcon,
    BookOpenTextIcon,
    EllipsisIcon,
    EyeClosedIcon,
    EyeIcon,
    NotebookPenIcon,
    NotebookTextIcon,
    ViewIcon,
    XIcon,
} from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useSentenceStore } from '../provider/SentenceProvider';

const LearningTool = observer((): React.ReactNode => {
    const sentenceStore = useSentenceStore();
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <section className="w-full">
            <div className="flex items-center justify-center gap-3">
                {/* 번역 보기/숨기기 */}
                <button
                    className={twMerge(
                        'btn btn-sm',
                        sentenceStore.isTranslationVisible &&
                            'bg-primary/10 text-primary border-primary/20',
                    )}
                    onClick={() => {
                        sentenceStore.toggleTranslationVisible();
                    }}
                >
                    {sentenceStore.isTranslationVisible ? (
                        <BookOpenTextIcon size={18} />
                    ) : (
                        <BookOpenIcon size={18} />
                    )}
                    번역보기
                </button>
                {/* 텍스트 보기 (3단계 순환) */}
                <button
                    className={twMerge(
                        'btn btn-sm',
                        sentenceStore.textVisibility === 'hidden' && '',
                        sentenceStore.textVisibility === 'hint' &&
                            'bg-primary/10 text-primary border-primary/20',
                        sentenceStore.textVisibility === 'visible' &&
                            'bg-primary/10 text-primary border-primary/20',
                    )}
                    onClick={() => {
                        sentenceStore.cycleTextVisibility();
                    }}
                >
                    {sentenceStore.textVisibility === 'hidden' ? (
                        <EyeClosedIcon size={18} />
                    ) : sentenceStore.textVisibility === 'hint' ? (
                        <ViewIcon size={18} />
                    ) : (
                        <EyeIcon size={18} />
                    )}
                    문장보기
                </button>
                {/* 학습 노트 보기/숨기기 */}
                <div className="relative">
                    {/* 인디케이터 (2개 이상일 때만) */}
                    {sentenceStore.learningNoteChunkIndexes.length > 0 && (
                        <div className="absolute -top-3.5 left-1/2 flex -translate-x-1/2">
                            {sentenceStore.learningNoteChunkIndexes.map((chunkIndex) => {
                                // 보이는 상태에서만 현재 인덱스 강조
                                const isHighlighted =
                                    chunkIndex === sentenceStore.currentLearningNoteChunkIndex;

                                return (
                                    <button
                                        key={chunkIndex}
                                        className={twMerge(
                                            'group flex h-3.5 w-3 items-center justify-center',
                                        )}
                                        onClick={() => {
                                            sentenceStore.setCurrentLearningNoteChunkIndex(
                                                chunkIndex,
                                            );
                                        }}
                                    >
                                        <div
                                            className={twMerge(
                                                'flex size-1.5 items-center justify-center rounded-full transition-colors',
                                                'group-hover:bg-primary/80',
                                                isHighlighted ? 'bg-primary/80' : 'bg-base-300',
                                            )}
                                        />
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    <button
                        className={twMerge(
                            'btn btn-sm',
                            !sentenceStore.hasLearningNotes && 'btn-disabled',
                            sentenceStore.currentLearningNoteChunkIndex >= 0 &&
                                'bg-primary/10 text-primary border-primary/20',
                        )}
                        onClick={() => {
                            sentenceStore.toggleLearningNoteVisible();
                        }}
                    >
                        {sentenceStore.currentLearningNoteChunkIndex >= 0 ? (
                            <NotebookPenIcon size={18} />
                        ) : (
                            <NotebookTextIcon size={18} />
                        )}
                        학습노트
                    </button>
                </div>

                {/* 더보기 버튼 */}
                <button
                    className={twMerge('btn btn-sm btn-circle')}
                    onClick={() => {
                        setIsExpanded(!isExpanded);
                    }}
                >
                    {isExpanded ? <XIcon size={14} /> : <EllipsisIcon size={14} />}
                </button>
            </div>
            {/* 확장 메뉴 */}
            <div className="mt-2 flex min-h-8 flex-row items-center justify-center gap-4">
                {isExpanded && (
                    <>
                        <label className="flex cursor-pointer items-center justify-between gap-1.5">
                            <input
                                type="checkbox"
                                className="toggle toggle-xs"
                                checked={sentenceStore.controlOption.translationAlwaysVisible}
                                onChange={(e) => {
                                    sentenceStore.setTranslationAlwaysVisible(e.target.checked);
                                }}
                            />
                            <span
                                className={twMerge(
                                    'text-base-content/60 text-xs',
                                    sentenceStore.controlOption.translationAlwaysVisible &&
                                        'text-base-content',
                                )}
                            >
                                번역 열어두기
                            </span>
                        </label>
                        <label className="flex cursor-pointer items-center justify-between gap-1.5">
                            <input
                                type="checkbox"
                                className="toggle toggle-xs"
                                checked={sentenceStore.controlOption.textAlwaysVisible}
                                onChange={(e) => {
                                    sentenceStore.setTextAlwaysVisible(e.target.checked);
                                }}
                            />
                            <span
                                className={twMerge(
                                    'text-base-content/60 text-xs',
                                    sentenceStore.controlOption.textAlwaysVisible &&
                                        'text-base-content',
                                )}
                            >
                                문장 열어두기
                            </span>
                        </label>
                    </>
                )}
            </div>
        </section>
    );
});

export default LearningTool;
